/**
 * Capa multi-comunidad (multi-tenant) para PIN CONECT.
 *
 * Modelo de datos en Firestore (proyecto pines-6297b):
 *
 *   comunidades/{comunidadId}
 *       nombre      string
 *       active      boolean   false = archivada
 *       createdAt   timestamp
 *       archivedAt  timestamp | null
 *
 *   usuarios/{codigo}          <-- el id del documento ES el codigo de ingreso
 *       name        string
 *       active      boolean
 *       comunidadId string      (ausente en datos viejos -> se migra)
 *
 *   usuarios/admin             <-- documento especial del SuperAdmin
 *       pass        string|number
 *       active      boolean
 *
 *   pines/{codigo}
 *       codigo      string
 *       valor       number      precio de venta (= plan)
 *       active      boolean     true = vendido
 *       pending     boolean|'take'
 *       fecha       timestamp   fecha de carga al inventario
 *       comunidadId string      comunidad dueña del inventario
 *       id          number      codigo del vendedor que lo vendio
 *       soldAt      timestamp   fecha de la venta
 *
 *   config/planes
 *       planes      [{ valor:number, label:string }]
 *
 * Nota importante: el codigo del vendedor es el id del documento en `usuarios`,
 * y SELLER/USERS inician sesion con solo ese codigo. Por eso los codigos deben
 * ser unicos a nivel global (no por comunidad) y numericos, porque en `pines.id`
 * se guardan como numero.
 */

import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  addDoc,
  Timestamp,
  serverTimestamp,
  getCountFromServer
} from 'firebase/firestore'
import { crearCuentaDeCodigo, darAcceso, correoDeCodigo } from '@/auth'

/** Planes que existian antes de que fueran configurables. Sirven de respaldo. */
export const PLANES_FALLBACK = [
  { valor: 500, label: 'PIN 1 hora' },
  { valor: 1000, label: 'PIN 3 horas' },
  { valor: 1500, label: 'PIN 12 horas' },
  { valor: 3000, label: 'PIN 24 horas' },
  { valor: 5000, label: 'PIN 2 dias' }
]

export const ID_COMUNIDAD_LEGACY_NOMBRE = 'Comunidad principal'

/* ------------------------------------------------------------------ */
/* Utilidades                                                          */
/* ------------------------------------------------------------------ */

/** El id del documento en `usuarios` siempre es string. */
export function codigoDocId (codigo) {
  return String(codigo).trim()
}

/** En `pines.id` el codigo del vendedor se guarda como numero. */
export function codigoNumero (codigo) {
  const n = parseInt(String(codigo).trim(), 10)
  return Number.isNaN(n) ? null : n
}

export function esCodigoValido (codigo) {
  return /^\d{3,10}$/.test(String(codigo || '').trim())
}

/** Ejecuta las promesas en tandas para no abrir cientos de conexiones a la vez. */
async function enTandas (tareas, tamano = 12) {
  const salida = []
  for (let i = 0; i < tareas.length; i += tamano) {
    const tanda = tareas.slice(i, i + tamano)
    salida.push(...await Promise.all(tanda.map(t => t())))
  }
  return salida
}

/* ------------------------------------------------------------------ */
/* Planes (denominaciones)                                             */
/* ------------------------------------------------------------------ */

function normalizarPlanes (planes) {
  if (!Array.isArray(planes)) return [...PLANES_FALLBACK]
  const limpios = planes
    .map(p => ({ valor: Number(p.valor), label: String(p.label || '').trim() }))
    .filter(p => Number.isFinite(p.valor) && p.valor > 0 && p.label)
  if (!limpios.length) return [...PLANES_FALLBACK]
  // sin valores repetidos y ordenados de menor a mayor
  const vistos = new Set()
  return limpios
    .filter(p => (vistos.has(p.valor) ? false : vistos.add(p.valor)))
    .sort((a, b) => a.valor - b.valor)
}

/** Lee la lista de planes configurada en el panel. */
export async function getPlanes () {
  const db = getFirestore()
  try {
    const snap = await getDoc(doc(db, 'config', 'planes'))
    if (snap.exists()) return normalizarPlanes(snap.data().planes)
  } catch (error) {
    console.error('No se pudieron leer los planes:', error)
  }
  return [...PLANES_FALLBACK]
}

/** Guarda la lista de planes. Solo la usa el panel de administracion. */
export async function guardarPlanes (planes) {
  const db = getFirestore()
  const normalizados = normalizarPlanes(planes)
  await setDoc(doc(db, 'config', 'planes'), {
    planes: normalizados,
    updatedAt: serverTimestamp()
  }, { merge: true })
  return normalizados
}

/**
 * Valores de pines que hay que tener en cuenta en los reportes: los planes
 * configurados ahora mas los que ya existen en la base de datos por ventas
 * historicas con precios antiguos.
 */
export function valoresParaReporte (planes, valoresExtra = []) {
  const set = new Set(planes.map(p => Number(p.valor)))
  PLANES_FALLBACK.forEach(p => set.add(p.valor))
  valoresExtra.forEach(v => { if (Number.isFinite(Number(v))) set.add(Number(v)) })
  return [...set].sort((a, b) => a - b)
}

/* ------------------------------------------------------------------ */
/* Comunidades                                                         */
/* ------------------------------------------------------------------ */

/** Lista de comunidades. Por defecto solo las activas. */
export async function getComunidades ({ incluirArchivadas = false } = {}) {
  const db = getFirestore()
  const snap = await getDocs(query(collection(db, 'comunidades'), orderBy('nombre')))
  // El spread va primero: si el documento tuviera un campo propio llamado
  // `id`, no debe pisar al id del documento.
  const todas = snap.docs.map(d => ({ ...d.data(), id: d.id }))
  return incluirArchivadas ? todas : todas.filter(c => c.active !== false)
}

export async function getComunidad (comunidadId) {
  if (!comunidadId) return null
  const db = getFirestore()
  const snap = await getDoc(doc(db, 'comunidades', comunidadId))
  return snap.exists() ? { ...snap.data(), id: snap.id } : null
}

/** ¿El codigo de ingreso ya esta usado por otro vendedor? */
export async function codigoDisponible (codigo) {
  const db = getFirestore()
  const id = codigoDocId(codigo)
  if (id === 'admin') return false
  const snap = await getDoc(doc(db, 'usuarios', id))
  return !snap.exists()
}

/**
 * Crea una comunidad y, en el mismo lote, sus vendedores.
 * `vendedores` = [{ nombre, codigo }]
 * Devuelve { id, vendedoresCreados }.
 * Lanza error si algun codigo esta repetido o ya existe.
 */
export async function crearComunidad ({ nombre, vendedores = [] }) {
  const db = getFirestore()
  const nombreLimpio = String(nombre || '').trim()
  if (!nombreLimpio) throw new Error('La comunidad necesita un nombre')

  // Validar codigos antes de escribir nada
  const limpios = vendedores
    .map(v => ({ nombre: String(v.nombre || '').trim(), codigo: codigoDocId(v.codigo) }))
    .filter(v => v.nombre || v.codigo)

  for (const v of limpios) {
    if (!v.nombre) throw new Error('Todos los vendedores necesitan nombre')
    if (!esCodigoValido(v.codigo)) {
      throw new Error(`El código de "${v.nombre}" debe ser numérico, de 3 a 10 dígitos`)
    }
  }

  const repetidos = limpios.map(v => v.codigo).filter((c, i, a) => a.indexOf(c) !== i)
  if (repetidos.length) throw new Error(`Código repetido en la lista: ${repetidos[0]}`)

  const ocupados = await enTandas(limpios.map(v => async () => ({
    codigo: v.codigo,
    libre: await codigoDisponible(v.codigo)
  })))
  const yaUsado = ocupados.find(o => !o.libre)
  if (yaUsado) throw new Error(`El código ${yaUsado.codigo} ya está en uso por otro vendedor`)

  const refComunidad = doc(collection(db, 'comunidades'))
  const batch = writeBatch(db)

  batch.set(refComunidad, {
    nombre: nombreLimpio,
    active: true,
    createdAt: serverTimestamp(),
    archivedAt: null
  })

  limpios.forEach(v => {
    batch.set(doc(db, 'usuarios', v.codigo), {
      name: v.nombre,
      active: true,
      comunidadId: refComunidad.id,
      createdAt: serverTimestamp()
    }, { merge: true })
  })

  await batch.commit()
  return { id: refComunidad.id, vendedoresCreados: limpios.length }
}

export async function renombrarComunidad (comunidadId, nombre) {
  const db = getFirestore()
  const nombreLimpio = String(nombre || '').trim()
  if (!nombreLimpio) throw new Error('La comunidad necesita un nombre')
  await updateDoc(doc(db, 'comunidades', comunidadId), { nombre: nombreLimpio })
}

/**
 * Archiva una comunidad: la saca de la lista activa y bloquea a sus vendedores,
 * pero conserva pines e historial para que el reporte general siga cuadrando.
 */
export async function archivarComunidad (comunidadId) {
  const db = getFirestore()
  const vendedores = await getVendedores(comunidadId)
  const batch = writeBatch(db)

  batch.set(doc(db, 'comunidades', comunidadId), {
    active: false,
    archivedAt: serverTimestamp()
  }, { merge: true })

  vendedores.forEach(v => {
    batch.set(doc(db, 'usuarios', v.codigo), { active: false }, { merge: true })
  })

  await batch.commit()
  return vendedores.length
}

/** Reactiva una comunidad archivada. Los vendedores se reactivan uno por uno. */
export async function restaurarComunidad (comunidadId) {
  const db = getFirestore()
  await setDoc(doc(db, 'comunidades', comunidadId), {
    active: true,
    archivedAt: null
  }, { merge: true })
}

/* ------------------------------------------------------------------ */
/* Vendedores                                                          */
/* ------------------------------------------------------------------ */

/** Vendedores de una comunidad; sin argumento devuelve todos (sin el admin). */
export async function getVendedores (comunidadId = null) {
  const db = getFirestore()
  const ref = collection(db, 'usuarios')
  const consulta = comunidadId
    ? query(ref, where('comunidadId', '==', comunidadId))
    : query(ref)

  const snap = await getDocs(consulta)
  return snap.docs
    .filter(d => d.id !== 'admin')
    .map(d => ({ ...d.data(), codigo: d.id }))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
}

/** Vendedores sin comunidad asignada (datos viejos). */
export async function getVendedoresSinComunidad () {
  const todos = await getVendedores()
  return todos.filter(v => !v.comunidadId)
}

export async function crearVendedor ({ nombre, codigo, comunidadId }) {
  const db = getFirestore()
  const nombreLimpio = String(nombre || '').trim()
  const id = codigoDocId(codigo)

  if (!nombreLimpio) throw new Error('Ingresa el nombre del vendedor')
  if (!esCodigoValido(id)) throw new Error('El código debe ser numérico, de 3 a 10 dígitos')
  if (!comunidadId) throw new Error('Elige la comunidad del vendedor')
  if (!await codigoDisponible(id)) throw new Error(`El código ${id} ya está en uso`)

  await setDoc(doc(db, 'usuarios', id), {
    name: nombreLimpio,
    active: true,
    comunidadId,
    createdAt: serverTimestamp()
  }, { merge: true })
}

/** Mueve un vendedor de comunidad sin perder sus ventas historicas. */
export async function asignarComunidadVendedor (codigo, comunidadId) {
  const db = getFirestore()
  await setDoc(doc(db, 'usuarios', codigoDocId(codigo)), { comunidadId }, { merge: true })
}

export async function eliminarVendedor (codigo) {
  const db = getFirestore()
  await deleteDoc(doc(db, 'usuarios', codigoDocId(codigo)))
}

/* ------------------------------------------------------------------ */
/* Reportes                                                           */
/* ------------------------------------------------------------------ */

/**
 * Cuenta documentos con agregacion del servidor: no descarga los pines, solo
 * el numero. El total en dinero se calcula como suma de valor x cantidad, que
 * es exacto porque cada pin guarda su propio `valor`.
 */
async function contar (filtros) {
  const db = getFirestore()
  const consulta = query(collection(db, 'pines'), ...filtros)
  const snap = await getCountFromServer(consulta)
  return snap.data().count
}

/**
 * Reporte de inventario y ventas.
 *   comunidadId: null  -> toda la operacion (reporte general)
 *   vendedor:    null  -> todos los vendedores de ese ambito
 *
 * Devuelve { vendidos, disponibles, recaudado, porVender, porValor }
 */
export async function reporte ({ comunidadId = null, vendedor = null, valores }) {
  const base = []
  if (comunidadId) base.push(where('comunidadId', '==', comunidadId))
  if (vendedor !== null && vendedor !== undefined) {
    const n = codigoNumero(vendedor)
    if (n === null) throw new Error('Código de vendedor inválido')
    base.push(where('id', '==', n))
  }

  const tareas = []
  valores.forEach(valor => {
    tareas.push(async () => ({
      valor,
      estado: 'vendidos',
      cantidad: await contar([...base, where('active', '==', true), where('valor', '==', valor)])
    }))
    // El inventario disponible no tiene vendedor, asi que solo se pide cuando
    // el reporte no esta filtrado por vendedor.
    if (vendedor === null || vendedor === undefined) {
      tareas.push(async () => ({
        valor,
        estado: 'disponibles',
        cantidad: await contar([...base, where('active', '==', false), where('valor', '==', valor)])
      }))
    }
  })

  const filas = await enTandas(tareas)

  const porValor = {}
  valores.forEach(v => { porValor[v] = { vendidos: 0, disponibles: 0 } })
  filas.forEach(f => { porValor[f.valor][f.estado] = f.cantidad })

  let vendidos = 0, disponibles = 0, recaudado = 0, porVender = 0
  valores.forEach(v => {
    vendidos += porValor[v].vendidos
    disponibles += porValor[v].disponibles
    recaudado += porValor[v].vendidos * v
    porVender += porValor[v].disponibles * v
  })

  return { vendidos, disponibles, recaudado, porVender, porValor }
}

/** Reporte de una comunidad con el desglose por cada uno de sus vendedores. */
export async function reporteComunidad (comunidadId, valores) {
  const [total, vendedores] = await Promise.all([
    reporte({ comunidadId, valores }),
    getVendedores(comunidadId)
  ])

  const desglose = await enTandas(vendedores.map(v => async () => ({
    ...v,
    ...await reporte({ comunidadId, vendedor: v.codigo, valores })
  })), 4)

  // Lo que vendio el administrador (u otro codigo) dentro de esta comunidad
  const atribuido = desglose.reduce((acc, v) => acc + v.recaudado, 0)
  const sinAtribuir = {
    recaudado: Math.max(0, total.recaudado - atribuido),
    vendidos: Math.max(0, total.vendidos - desglose.reduce((a, v) => a + v.vendidos, 0))
  }

  return { total, vendedores: desglose.sort((a, b) => b.recaudado - a.recaudado), sinAtribuir }
}

/** Reporte general: total de la operacion y una fila por comunidad. */
export async function reporteGeneral (valores, { incluirArchivadas = true } = {}) {
  const comunidades = await getComunidades({ incluirArchivadas })

  const filas = await enTandas(comunidades.map(c => async () => ({
    ...c,
    ...await reporte({ comunidadId: c.id, valores })
  })), 4)

  const total = await reporte({ valores })

  // Pines que todavia no pertenecen a ninguna comunidad (datos sin migrar)
  const asignado = filas.reduce((acc, f) => ({
    vendidos: acc.vendidos + f.vendidos,
    disponibles: acc.disponibles + f.disponibles,
    recaudado: acc.recaudado + f.recaudado,
    porVender: acc.porVender + f.porVender
  }), { vendidos: 0, disponibles: 0, recaudado: 0, porVender: 0 })

  const sinComunidad = {
    vendidos: Math.max(0, total.vendidos - asignado.vendidos),
    disponibles: Math.max(0, total.disponibles - asignado.disponibles),
    recaudado: Math.max(0, total.recaudado - asignado.recaudado),
    porVender: Math.max(0, total.porVender - asignado.porVender)
  }

  return {
    total,
    comunidades: filas.sort((a, b) => b.recaudado - a.recaudado),
    sinComunidad
  }
}

/* ------------------------------------------------------------------ */
/* Migracion de los datos que existen desde antes                      */
/* ------------------------------------------------------------------ */

/**
 * Cuenta exactamente los pines sin `comunidadId`.
 *
 * Firestore no puede consultar "este campo no existe", asi que se cuenta el
 * total de la coleccion y se le resta lo que si esta asignado a alguna
 * comunidad. Son conteos del servidor: no descarga los documentos.
 */
export async function contarPinesSinComunidad () {
  const comunidades = await getComunidades({ incluirArchivadas: true })
  const [total, asignados] = await Promise.all([
    contar([]),
    enTandas(comunidades.map(c => () => contar([where('comunidadId', '==', c.id)])))
  ])
  return Math.max(0, total - asignados.reduce((a, b) => a + b, 0))
}

/** ¿Quedan pines o vendedores sin comunidad? */
export async function pendientesDeMigrar () {
  const [pines, vendedores] = await Promise.all([
    contarPinesSinComunidad(),
    getVendedoresSinComunidad()
  ])
  return {
    pines,
    vendedores: vendedores.length,
    hay: pines > 0 || vendedores.length > 0
  }
}

/**
 * Asigna todos los pines y vendedores sin comunidad a `comunidadId`.
 * Se ejecuta por tandas de 400 documentos; `onProgreso` recibe el acumulado.
 * Es idempotente: solo toca los documentos a los que les falta el campo.
 */
export async function migrarALaComunidad (comunidadId, onProgreso = () => {}) {
  const db = getFirestore()
  if (!comunidadId) throw new Error('Elige la comunidad destino')

  let migrados = 0

  // Vendedores: ademas de asignarles comunidad, se les crea la cuenta con la
  // que van a entrar y el permiso que leen las reglas de seguridad. Antes el
  // codigo vivia en un documento legible; ahora vive cifrado en Authentication.
  const vendedores = await getVendedoresSinComunidad()
  for (const v of vendedores) {
    await crearCuentaDeCodigo(v.codigo)

    await darAcceso({
      correo: correoDeCodigo(v.codigo),
      rol: 'vendedor',
      proveedor: 'password',
      nombre: v.name || '',
      comunidadId,
      codigo: v.codigo
    })

    await setDoc(doc(db, 'usuarios', v.codigo), { comunidadId }, { merge: true })

    migrados += 1
    onProgreso({ fase: 'vendedores', migrados })
  }

  // Pines: se recorre la coleccion completa en paginas
  let ultimo = null
  let pinesMigrados = 0
  for (;;) {
    const consulta = ultimo
      ? query(collection(db, 'pines'), orderBy('__name__'), startAfter(ultimo), limit(400))
      : query(collection(db, 'pines'), orderBy('__name__'), limit(400))

    const snap = await getDocs(consulta)
    if (snap.empty) break

    const faltantes = snap.docs.filter(d => !d.data().comunidadId)
    if (faltantes.length) {
      const batch = writeBatch(db)
      faltantes.forEach(d => batch.set(d.ref, { comunidadId }, { merge: true }))
      await batch.commit()
      pinesMigrados += faltantes.length
      onProgreso({ fase: 'pines', migrados: pinesMigrados })
    }

    ultimo = snap.docs[snap.docs.length - 1]
    if (snap.docs.length < 400) break
  }

  return { vendedores: vendedores.length, pines: pinesMigrados }
}

/* ================================================================== */
/* Costos, balance neto e inversionistas                              */
/* ================================================================== */

/*
 * Un periodo es un mes calendario, en texto 'YYYY-MM'. El balance de una
 * comunidad en un periodo es:
 *
 *     ingresos (pines vendidos ese mes)  -  costos registrados ese mes
 *     = neto repartible
 *
 * y el rédito de cada inversionista es su porcentaje de ese neto.
 *
 * Importante sobre los datos viejos: `soldAt` se empezó a guardar al
 * introducir las comunidades. Las ventas anteriores no tienen fecha de venta,
 * así que NO se pueden asignar a ningún mes. Se reportan aparte como
 * "histórico" en vez de repartirlas a un periodo al que quizá no pertenecen.
 */

/* ---------------------------- Periodos ---------------------------- */

/** 'YYYY-MM' del mes de una fecha (por defecto, hoy). */
export function periodoDe (fecha = new Date()) {
  const y = fecha.getFullYear()
  const m = String(fecha.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/** Límites [inicio, fin) de un periodo, como Timestamp de Firestore. */
export function rangoPeriodo (periodo) {
  const [y, m] = String(periodo).split('-').map(Number)
  const inicio = new Date(y, m - 1, 1, 0, 0, 0, 0)
  const fin = new Date(y, m, 1, 0, 0, 0, 0)
  return { inicio: Timestamp.fromDate(inicio), fin: Timestamp.fromDate(fin) }
}

/** Nombre legible: '2026-10' -> 'octubre de 2026'. */
export function nombrePeriodo (periodo) {
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
  const [y, m] = String(periodo).split('-').map(Number)
  if (!y || !m) return periodo
  return `${meses[m - 1]} de ${y}`
}

/** Los últimos `cantidad` periodos, del más reciente al más antiguo. */
export function ultimosPeriodos (cantidad = 12, desde = new Date()) {
  const lista = []
  const d = new Date(desde.getFullYear(), desde.getMonth(), 1)
  for (let i = 0; i < cantidad; i++) {
    lista.push(periodoDe(d))
    d.setMonth(d.getMonth() - 1)
  }
  return lista
}

/* ----------------------------- Costos ----------------------------- */

export const CATEGORIAS_COSTO = [
  { id: 'internet', label: 'Internet / proveedor' },
  { id: 'mantenimiento', label: 'Mantenimiento' },
  { id: 'equipos', label: 'Equipos' },
  { id: 'energia', label: 'Energía' },
  { id: 'personal', label: 'Personal' },
  { id: 'otro', label: 'Otro' }
]

export async function getCostos (comunidadId, periodo) {
  const db = getFirestore()
  const snap = await getDocs(query(
    collection(db, 'costos'),
    where('comunidadId', '==', comunidadId),
    where('periodo', '==', periodo)
  ))
  return snap.docs
    .map(d => ({ ...d.data(), docId: d.id }))
    .sort((a, b) => (b.creadoEn?.seconds || 0) - (a.creadoEn?.seconds || 0))
}

export async function agregarCosto ({ comunidadId, periodo, concepto, categoria, monto, nota }) {
  const db = getFirestore()
  const limpio = String(concepto || '').trim()
  const valor = Number(monto)

  if (!comunidadId) throw new Error('Elige la comunidad del costo')
  if (!limpio) throw new Error('Escribe en qué se gastó')
  if (!Number.isFinite(valor) || valor <= 0) throw new Error('El monto debe ser mayor que cero')

  const ref = await addDoc(collection(db, 'costos'), {
    comunidadId,
    periodo: periodo || periodoDe(),
    concepto: limpio,
    categoria: categoria || 'otro',
    monto: Math.round(valor),
    nota: String(nota || '').trim(),
    creadoEn: serverTimestamp()
  })
  return ref.id
}

export async function eliminarCosto (docId) {
  const db = getFirestore()
  await deleteDoc(doc(db, 'costos', docId))
}

export function sumarCostos (costos) {
  return costos.reduce((acc, c) => acc + (Number(c.monto) || 0), 0)
}

/* --------------------- Ingresos de un periodo --------------------- */

/**
 * Ingresos de una comunidad en un periodo, contando solo las ventas con fecha
 * (`soldAt`). Usa conteos del servidor: suma = Σ valor × cantidad.
 */
export async function ingresosPeriodo (comunidadId, periodo, valores) {
  const db = getFirestore()
  const { inicio, fin } = rangoPeriodo(periodo)

  const tareas = valores.map(valor => async () => {
    const consulta = query(
      collection(db, 'pines'),
      where('comunidadId', '==', comunidadId),
      where('active', '==', true),
      where('valor', '==', valor),
      where('soldAt', '>=', inicio),
      where('soldAt', '<', fin)
    )
    const snap = await getCountFromServer(consulta)
    return { valor, cantidad: snap.data().count }
  })

  const filas = await enTandas(tareas)
  const porValor = {}
  let total = 0
  let unidades = 0
  filas.forEach(f => {
    porValor[f.valor] = f.cantidad
    total += f.cantidad * f.valor
    unidades += f.cantidad
  })

  return { total, unidades, porValor }
}

/**
 * Ventas sin `soldAt`: son las anteriores a que se empezara a guardar la fecha
 * de venta. No pertenecen a ningún periodo y se reportan aparte.
 */
export async function ventasSinFecha (comunidadId, valores) {
  const db = getFirestore()

  const totalVendidos = await contar([
    where('comunidadId', '==', comunidadId),
    where('active', '==', true)
  ])

  // Los que sí tienen fecha: un rango abierto sobre soldAt los filtra, porque
  // un documento sin el campo nunca entra en una consulta de rango sobre él.
  const conFecha = await contar([
    where('comunidadId', '==', comunidadId),
    where('active', '==', true),
    where('soldAt', '>=', Timestamp.fromDate(new Date(2000, 0, 1)))
  ])

  const sinFecha = Math.max(0, totalVendidos - conFecha)
  if (!sinFecha) return { unidades: 0, total: 0 }

  // Para el monto hay que mirar por denominación
  const tareas = valores.map(valor => async () => {
    const [todos, fechados] = await Promise.all([
      contar([
        where('comunidadId', '==', comunidadId),
        where('active', '==', true),
        where('valor', '==', valor)
      ]),
      contar([
        where('comunidadId', '==', comunidadId),
        where('active', '==', true),
        where('valor', '==', valor),
        where('soldAt', '>=', Timestamp.fromDate(new Date(2000, 0, 1)))
      ])
    ])
    return { valor, cantidad: Math.max(0, todos - fechados) }
  })

  const filas = await enTandas(tareas)
  const total = filas.reduce((acc, f) => acc + f.cantidad * f.valor, 0)
  return { unidades: sinFecha, total }
}

/* ----------------------------- Balance ---------------------------- */

/**
 * Balance neto de una comunidad en un periodo.
 * Devuelve { ingresos, costos, neto, detalleCostos, unidades }
 */
export async function balancePeriodo (comunidadId, periodo, valores) {
  const [ingresos, detalleCostos] = await Promise.all([
    ingresosPeriodo(comunidadId, periodo, valores),
    getCostos(comunidadId, periodo)
  ])
  const costos = sumarCostos(detalleCostos)
  return {
    periodo,
    ingresos: ingresos.total,
    unidades: ingresos.unidades,
    costos,
    neto: ingresos.total - costos,
    detalleCostos
  }
}

/** Balance de varios periodos seguidos, para la gráfica y el histórico. */
export async function balanceVariosPeriodos (comunidadId, periodos, valores) {
  return enTandas(periodos.map(p => () => balancePeriodo(comunidadId, p, valores)), 3)
}

/* -------------------------- Inversionistas ------------------------ */

export async function getInversionistas (comunidadId = null) {
  const db = getFirestore()
  const ref = collection(db, 'inversionistas')
  const consulta = comunidadId
    ? query(ref, where('comunidadId', '==', comunidadId))
    : query(ref)
  const snap = await getDocs(consulta)
  return snap.docs
    .map(d => ({ ...d.data(), codigo: d.id }))
    .sort((a, b) => (Number(b.porcentaje) || 0) - (Number(a.porcentaje) || 0))
}

export async function getInversionista (codigo) {
  const db = getFirestore()
  const snap = await getDoc(doc(db, 'inversionistas', codigoDocId(codigo)))
  return snap.exists() ? { ...snap.data(), codigo: snap.id } : null
}

/**
 * ¿El código está libre? Se revisa contra vendedores y contra inversionistas:
 * son apps distintas, pero un código repetido confunde a quien administra.
 */
export async function codigoDisponibleGlobal (codigo) {
  const db = getFirestore()
  const id = codigoDocId(codigo)
  if (id === 'admin') return false
  const [u, i] = await Promise.all([
    getDoc(doc(db, 'usuarios', id)),
    getDoc(doc(db, 'inversionistas', id))
  ])
  return !u.exists() && !i.exists()
}

/** Suma de porcentajes ya comprometidos en una comunidad. */
export async function porcentajeComprometido (comunidadId, excluirCodigo = null) {
  const lista = await getInversionistas(comunidadId)
  return lista
    .filter(i => i.codigo !== excluirCodigo)
    .reduce((acc, i) => acc + (Number(i.porcentaje) || 0), 0)
}

export async function crearInversionista ({ nombre, codigo, comunidadId, proyecto, correo, porcentaje, montoInvertido, desde }) {
  const db = getFirestore()
  const id = codigoDocId(codigo)
  const nombreLimpio = String(nombre || '').trim()
  const pct = Number(porcentaje)

  if (!nombreLimpio) throw new Error('Ingresa el nombre del inversionista')
  if (!esCodigoValido(id)) throw new Error('El código debe ser numérico, de 3 a 10 dígitos')
  if (!comunidadId) throw new Error('Elige la comunidad en la que invirtió')
  if (!Number.isFinite(pct) || pct <= 0 || pct > 100) throw new Error('El porcentaje debe estar entre 1 y 100')

  const comprometido = await porcentajeComprometido(comunidadId)
  if (comprometido + pct > 100) {
    throw new Error(`Esta comunidad ya tiene ${comprometido}% repartido; solo quedan ${100 - comprometido}%`)
  }

  await setDoc(doc(db, 'inversionistas', id), {
    nombre: nombreLimpio,
    active: true,
    comunidadId,
    correo: String(correo || '').trim().toLowerCase(),
    proyecto: String(proyecto || '').trim(),
    porcentaje: pct,
    montoInvertido: Math.round(Number(montoInvertido) || 0),
    desde: desde || periodoDe(),
    createdAt: serverTimestamp()
  })
}

export async function actualizarInversionista (codigo, cambios) {
  const db = getFirestore()
  const id = codigoDocId(codigo)

  if (cambios.porcentaje !== undefined) {
    const pct = Number(cambios.porcentaje)
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      throw new Error('El porcentaje debe estar entre 1 y 100')
    }
    const actual = await getInversionista(id)
    const comunidadId = cambios.comunidadId || (actual && actual.comunidadId)
    const comprometido = await porcentajeComprometido(comunidadId, id)
    if (comprometido + pct > 100) {
      throw new Error(`Esta comunidad ya tiene ${comprometido}% repartido; solo quedan ${100 - comprometido}%`)
    }
    cambios.porcentaje = pct
  }

  await setDoc(doc(db, 'inversionistas', id), cambios, { merge: true })
}

export async function eliminarInversionista (codigo) {
  const db = getFirestore()
  await deleteDoc(doc(db, 'inversionistas', codigoDocId(codigo)))
}

/* ----------------------------- Pagos ------------------------------ */

export async function getPagos (codigoInversionista) {
  const db = getFirestore()
  const snap = await getDocs(query(
    collection(db, 'pagosInversionista'),
    where('inversionista', '==', codigoDocId(codigoInversionista))
  ))
  return snap.docs
    .map(d => ({ ...d.data(), docId: d.id }))
    .sort((a, b) => (b.fecha?.seconds || 0) - (a.fecha?.seconds || 0))
}

export async function registrarPago ({ inversionista, comunidadId, monto, nota, periodo }) {
  const db = getFirestore()
  const valor = Number(monto)
  if (!Number.isFinite(valor) || valor <= 0) throw new Error('El monto debe ser mayor que cero')

  const ref = await addDoc(collection(db, 'pagosInversionista'), {
    inversionista: codigoDocId(inversionista),
    comunidadId: comunidadId || '',
    monto: Math.round(valor),
    nota: String(nota || '').trim(),
    periodo: periodo || null,
    fecha: serverTimestamp()
  })
  return ref.id
}

export async function eliminarPago (docId) {
  const db = getFirestore()
  await deleteDoc(doc(db, 'pagosInversionista', docId))
}

/* ---------------- Estado de cuenta del inversionista -------------- */

/**
 * Estados de cuenta de VARIOS inversionistas de una sola pasada.
 *
 * Los inversionistas de una misma comunidad comparten exactamente el mismo
 * balance mensual: solo cambia el porcentaje que se le aplica. Calcularlo una
 * vez por comunidad en vez de una vez por inversionista evita repetir decenas
 * de consultas idénticas.
 */
export async function estadosDeCuenta (inversionistas, valores, { meses = 6 } = {}) {
  // Agrupar por comunidad y quedarse con el rango de meses más amplio que
  // pida cualquiera de sus inversionistas.
  const porComunidad = new Map()
  inversionistas.forEach(inv => {
    const actual = porComunidad.get(inv.comunidadId) || { inversionistas: [], desde: null }
    actual.inversionistas.push(inv)
    if (!actual.desde || (inv.desde && inv.desde < actual.desde)) actual.desde = inv.desde
    porComunidad.set(inv.comunidadId, actual)
  })

  const ventana = ultimosPeriodos(meses)

  const balancesPorComunidad = new Map()
  for (const [comunidadId, grupo] of porComunidad) {
    const periodos = ventana.filter(p => !grupo.desde || p >= grupo.desde)
    const balances = await balanceVariosPeriodos(comunidadId, periodos, valores)
    balancesPorComunidad.set(comunidadId, balances)
  }

  const comunidades = new Map()
  await Promise.all([...porComunidad.keys()].map(async id => {
    comunidades.set(id, await getComunidad(id))
  }))

  return Promise.all(inversionistas.map(async inv => {
    const pct = (Number(inv.porcentaje) || 0) / 100
    const balances = balancesPorComunidad.get(inv.comunidadId) || []
    const periodos = balances
      .filter(b => !inv.desde || b.periodo >= inv.desde)
      .map(b => ({ ...b, redito: Math.round(b.neto * pct) }))

    const pagos = await getPagos(inv.codigo)
    const acumulado = periodos.reduce((acc, f) => acc + f.redito, 0)
    const pagado = pagos.reduce((acc, p) => acc + (Number(p.monto) || 0), 0)

    return {
      inversionista: inv,
      comunidad: comunidades.get(inv.comunidadId) || null,
      periodos,
      pagos,
      acumulado,
      pagado,
      saldo: acumulado - pagado
    }
  }))
}

/**
 * Lo que le corresponde a un inversionista, mes a mes.
 *
 * Solo cuentan los periodos desde `desde` (cuando entró) en adelante: no
 * recibe réditos de meses anteriores a su entrada.
 *
 * Devuelve { inversionista, comunidad, periodos, acumulado, pagado, saldo }
 */
export async function estadoCuentaInversionista (codigo, valores, { meses = 6 } = {}) {
  const inv = await getInversionista(codigo)
  if (!inv) throw new Error('No se encontró el inversionista')

  const comunidad = await getComunidad(inv.comunidadId)
  const pct = (Number(inv.porcentaje) || 0) / 100

  const periodos = ultimosPeriodos(meses)
    .filter(p => !inv.desde || p >= inv.desde)

  const [balances, pagos] = await Promise.all([
    balanceVariosPeriodos(inv.comunidadId, periodos, valores),
    getPagos(codigo)
  ])

  const filas = balances.map(b => ({
    ...b,
    redito: Math.round(b.neto * pct)
  }))

  const acumulado = filas.reduce((acc, f) => acc + f.redito, 0)
  const pagado = pagos.reduce((acc, p) => acc + (Number(p.monto) || 0), 0)

  return {
    inversionista: inv,
    comunidad,
    periodos: filas,
    pagos,
    acumulado,
    pagado,
    saldo: acumulado - pagado
  }
}
